import { useLocation, Link } from "react-router-dom";
import { Check, Info } from "lucide-react";
import { SERVICE_PAGES, PAGE_TITLES } from "@/data/pages";
import { productsFor } from "@/data/products";
import { faqList } from "@/data/faqs";
import { Seo, JsonLd, serviceLd } from "@/components/site/Seo";
import { Breadcrumbs, Container, Eyebrow, MonogramPlaceholder, PhotoFrame, QuoteButton, Reveal, SectionHeading, WhatsAppButton } from "@/components/site/Primitives";
import { FaqSection, FinalCta, HowToOrder, ProductGrid, RelatedLinks } from "@/components/site/Blocks";
import QuoteForm from "@/components/site/QuoteForm";
import NotFound from "./NotFound";

export default function ServicePage() {
  const { pathname } = useLocation();
  const page = SERVICE_PAGES[pathname.replace(/^\//, "").replace(/\/$/, "")];
  if (!page) return <NotFound />;
  const products = page.productSet ? productsFor(page.productSet) : [];
  const faqs = faqList(page.faqKeys);
  const [m1, m2] = page.hero;

  return (
    <>
      <Seo title={page.seoTitle} description={page.seoDescription} path={page.path} image={m1 ? m1.large : undefined} />
      <JsonLd data={serviceLd({ name: page.serviceType, description: page.seoDescription, path: page.path, products })} />

      {/* Hero */}
      <section className={page.cultural ? "bg-[#161616] text-white" : "bg-white"} data-testid="service-hero">
        <Container className="pt-6">
          <div className={page.cultural ? "[&_*]:!text-white/60 [&_a:hover]:!text-white" : ""}><Breadcrumbs items={[{ name: page.breadcrumb, path: page.path }]} /></div>
        </Container>
        <Container className="grid gap-12 pb-16 pt-8 lg:grid-cols-12 lg:items-center lg:pb-24 lg:pt-12">
          <div className="lg:col-span-6">
            <Eyebrow light={page.cultural}>{page.eyebrow}</Eyebrow>
            <h1 className={`kl-h1 mt-5 ${page.cultural ? "text-white" : ""}`}>{page.h1}</h1>
            <p className={`mt-6 max-w-xl text-base leading-7 sm:text-lg sm:leading-8 ${page.cultural ? "text-white/72" : "text-black/70"}`}>{page.intro}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <a href="#quote-form" className={`kl-btn h-12 px-6 ${page.cultural ? "kl-btn-light" : "kl-btn-dark"}`} data-testid="service-hero-quote-button">{page.primaryCta}</a>
              <WhatsAppButton messageKey={page.whatsapp} variant={page.cultural ? "ghost-light" : "outline"} className="h-12" testId="service-hero-whatsapp-button" />
            </div>
            {page.secondaryCta ? (
              <Link to={`/contact?category=${page.category}&product=${encodeURIComponent(page.secondaryCta.product)}#quote-form`} className="mt-5 inline-block text-sm font-semibold underline underline-offset-4" data-testid="service-hero-secondary-cta">{page.secondaryCta.label}</Link>
            ) : null}
          </div>
          <div className="lg:col-span-6">
            {m1 && m2 ? (
              <div className="grid grid-cols-2 items-start gap-4">
                <PhotoFrame media={m1} ratio="4/5" eager className={page.cultural ? "!border-white/10 !bg-white/5" : ""} sizes="(min-width:1024px) 24vw, 46vw" />
                <PhotoFrame media={m2} ratio="4/5" className={`mt-10 ${page.cultural ? "!border-white/10 !bg-white/5" : ""}`} sizes="(min-width:1024px) 24vw, 46vw" />
              </div>
            ) : m1 ? (
              <PhotoFrame media={m1} ratio="4/3" eager sizes="(min-width:1024px) 45vw, 92vw" />
            ) : (
              <MonogramPlaceholder ratio="4/3" label="Project photos coming soon" />
            )}
          </div>
        </Container>
      </section>

      {/* Content sections */}
      {page.sections.map((s, idx) => (
        <section key={s.h2} className={idx % 2 === 0 ? "bg-[#F7F7F7] py-16 lg:py-24" : "bg-white py-16 lg:py-24"} aria-labelledby={`sec-${idx}`}>
          <Container>
            <SectionHeading title={s.h2} lead={s.lead} id={`sec-${idx}`} />
            {s.items ? (
              <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {s.items.map((it, i) => (
                  <Reveal key={it.title} delay={(i % 3) * 70} className="kl-card p-6">
                    <h3 className="text-lg font-semibold tracking-tight">{it.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-black/65">{it.text}</p>
                  </Reveal>
                ))}
              </div>
            ) : null}
            {s.checklist ? (
              <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:max-w-3xl">
                {s.checklist.map((c) => (
                  <li key={c} className="flex items-start gap-3 rounded-[12px] border border-black/10 bg-white px-4 py-3 text-[15px]"><Check className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /> {c}</li>
                ))}
              </ul>
            ) : null}
            {s.links ? (
              <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {s.links.map((l) => (
                  <li key={l.to}><Link to={l.to} className="kl-card flex items-center justify-between px-5 py-4 font-semibold" data-testid={`service-internal-link-${l.to.replace("/", "")}`}>{l.label} <span aria-hidden="true">→</span></Link></li>
                ))}
              </ul>
            ) : null}
          </Container>
        </section>
      ))}

      {page.note ? (
        <Container className="py-8">
          <p className="flex items-start gap-3 rounded-[14px] border border-black/10 bg-white p-5 text-sm leading-6 text-black/70" data-testid="service-note"><Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /> {page.note}</p>
        </Container>
      ) : null}

      {products.length ? <ProductGrid products={products} title={page.productsTitle} /> : null}

      <HowToOrder />

      {/* Quote form */}
      <section id="quote-form" className="scroll-mt-24 bg-[#F7F7F7] py-16 lg:py-24" aria-labelledby="form-title">
        <Container className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHeading eyebrow="Get a quotation" title={page.primaryCta} lead="Share your requirements and KhozaLand will reply with a personalised quotation. No payment is taken online." id="form-title" />
            <WhatsAppButton messageKey={page.whatsapp} className="mt-6" label="Or WhatsApp your request" testId="service-form-whatsapp-button" />
          </div>
          <div className="lg:col-span-8">
            <QuoteForm defaultCategory={page.category} sourcePage={page.path} />
          </div>
        </Container>
      </section>

      <FaqSection faqs={faqs} />
      <RelatedLinks links={page.related} titles={PAGE_TITLES} />
      <FinalCta category={page.category} messageKey={page.whatsapp} primaryLabel={page.primaryCta} />
    </>
  );
}

export const ServiceQuoteButton = QuoteButton;
